// @section 1 Mission-briefing drawer — slides in from right.
// Reads the active briefing from <DrawerProvider>. Mounted once at the AppShell
// level. Esc closes; backdrop click closes. Animation is CSS (.drawer.open).

import { useEffect } from 'react';
import { useDrawer } from './DrawerContext';

export function Drawer() {
  const { open, briefing, close } = useDrawer();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <aside className={`drawer${open ? ' open' : ''}`} aria-hidden={!open}>
      {briefing && (
        <>
          <button className="drawer-close" onClick={close} aria-label="Close briefing">×</button>

          <div className="brief-head">
            <div className="eyebrow">
              <span className={`stage-pill ${briefing.stage}`}>{briefing.stage}</span>
              {briefing.urgency && <span className="urgency-pill">{briefing.urgency}</span>}
            </div>
            <h2>{briefing.title}</h2>
            <div className="meta-row">
              {briefing.meta.map((m, i) => (
                <span className="item" key={i}>
                  <span className="lbl">{m.label}</span>
                  <b>{m.value}</b>
                </span>
              ))}
            </div>
          </div>

          {briefing.situation && (
            <div className="brief-section">
              <h4>Situation</h4>
              <p>{briefing.situation}</p>
            </div>
          )}

          {briefing.whatChanged && (
            <div className="brief-section">
              <h4>What changed</h4>
              <p>{briefing.whatChanged}</p>
            </div>
          )}

          {briefing.whyItMatters && (
            <div className="brief-section">
              <h4>Why it matters</h4>
              <p>{briefing.whyItMatters}</p>
            </div>
          )}

          {briefing.recommendedMove && (
            <div className="brief-section">
              <h4>Recommended next move</h4>
              <div className="recommend">
                <div className="move">{briefing.recommendedMove.move}</div>
                <div className="reason">{briefing.recommendedMove.reason}</div>
              </div>
            </div>
          )}

          {briefing.evidence && briefing.evidence.length > 0 && (
            <div className="brief-section">
              <h4>Supporting evidence</h4>
              <ul className="evidence-list">
                {briefing.evidence.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {briefing.actions && briefing.actions.length > 0 && (
            <div className="brief-actions">
              {briefing.actions.map((a, i) => (
                <button
                  key={i}
                  className={`btn${a.kind === 'primary' ? ' primary' : ''}`}
                  onClick={a.onClick}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </aside>
  );
}
